import React, { useState, useEffect, useRef, useCallback } from "react";
import { debounce, DebouncedFunc } from "lodash";
import * as Sentry from "@sentry/react";

import { BasePaginationQueryParams, PaginatedResponse } from "@/redux/types";
import { PAGINATION } from "@/shared/utils/constants";
import { InfiniteOptionsHookConfig, Option } from "../types";

const useInfiniteOptions = <T, C extends BasePaginationQueryParams>({
  fetchFn,
  defaultFilters,
  labelFieldName,
  labelFormatter,
  onFetchSuccess,
  valueFieldName,
  searchFieldName,
  lookupFieldName,
  debounceMs = 300,
  initialValue,
}: InfiniteOptionsHookConfig<T, C>) => {
  const [options, setOptions] = useState<(T & Option)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number | null>(1);
  const [initialValueFetched, setInitialValueFetched] = useState(false);

  const fetchRef = useRef(0);
  const optionsRef = useRef<(T & Option)[]>([]);
  const debouncedSearchRef = useRef<DebouncedFunc<
    (searchValue: string) => void
  > | null>(null);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    if (!labelFormatter) return;
    setOptions((prev) =>
      prev.map((opt) => ({ ...opt, label: labelFormatter(opt) })),
    );
  }, [labelFormatter]);

  const getLabel = useCallback(
    (item: T): string => {
      if (labelFormatter) {
        return labelFormatter(item);
      }
      return String(item[labelFieldName as keyof T]);
    },
    [labelFormatter, labelFieldName],
  );

  const fetchData = useCallback(
    async (params: C, isSearching = false) => {
      setIsLoading(true);
      fetchRef.current += 1;
      const fetchId = fetchRef.current;

      try {
        const response = await fetchFn(params);

        // Ignore if not the latest request
        if (fetchId !== fetchRef.current) return;

        const responseData = response.data as PaginatedResponse<T>;
        const results = responseData?.results || [];
        onFetchSuccess?.(results);
        const newOptions = results
          .filter(
            (item) =>
              item &&
              (labelFormatter ||
                item[labelFieldName as keyof T] !== undefined) &&
              item[valueFieldName as keyof T] !== undefined,
          )
          .map((item) => ({
            ...item,
            label: getLabel(item),
            value: item[valueFieldName as keyof T] as string | number,
          }));

        setOptions((prev) => {
          if (isSearching) {
            return newOptions;
          } else {
            return [
              ...prev,
              ...newOptions.filter(
                (newOpt: Option) =>
                  !prev.some(
                    (existingOpt) => existingOpt.value === newOpt.value,
                  ),
              ),
            ];
          }
        });

        setPage(responseData?.next || null);
      } catch (error) {
        setPage(null);
        Sentry.captureException(error, {
          extra: {
            params,
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFn, labelFieldName, valueFieldName, getLabel],
  );

  const doSearch = debounce((searchValue: string) => {
    const params = {
      ...(defaultFilters || {}),
      [searchFieldName]: searchValue
        ? encodeURIComponent(searchValue)
        : undefined,
      page: PAGINATION.PAGE.DEFAULT,
    } as C;

    setOptions([]);
    fetchData(params, true);
  }, debounceMs);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(null);
    fetchRef.current += 1;

    if (debouncedSearchRef.current) {
      debouncedSearchRef.current.cancel();
    }
    debouncedSearchRef.current = doSearch;
    debouncedSearchRef.current(value);
  }, []);

  const fetchMoreOptions = useCallback(() => {
    if (!page) return;

    const params = {
      ...(defaultFilters || {}),
      [searchFieldName]: search ? encodeURIComponent(search) : undefined,
      page,
    } as C;

    fetchData(params, false);
  }, [fetchData, defaultFilters, page, search, searchFieldName]);

  const fetchInitialValues = useCallback(async () => {
    if (
      !initialValue ||
      (Array.isArray(initialValue) && initialValue.length === 0) ||
      initialValueFetched
    )
      return;

    const values = Array.isArray(initialValue) ? initialValue : [initialValue];

    const allPresent = values.every((val) =>
      optionsRef.current.some((opt) => String(opt.value) === String(val)),
    );
    if (allPresent) {
      setInitialValueFetched(true);
      return;
    }

    const lookupValues = values.filter(
      (val) => typeof val === "number" || !isNaN(Number(val)),
    );

    if (lookupValues.length === 0) {
      setInitialValueFetched(true);
      return;
    }

    setIsLoading(true);
    try {
      // @ts-expect-error supress pagination params on first request
      const response = await fetchFn({
        ...(defaultFilters || {}),
        [lookupFieldName]: lookupValues,
        pagination: "off",
      });

      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      onFetchSuccess?.(results);
      const initialOptions = results
        .filter(
          (item) =>
            item &&
            (labelFormatter || item[labelFieldName as keyof T] !== undefined) &&
            item[valueFieldName as keyof T] !== undefined,
        )
        .map((item) => ({
          ...item,
          label: getLabel(item),
          value: item[valueFieldName as keyof T] as string | number,
        }));

      setOptions(initialOptions);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          initialValue,
          defaultFilters,
        },
      });
    } finally {
      setIsLoading(false);
      setInitialValueFetched(true);
    }
  }, [
    fetchFn,
    defaultFilters,
    initialValue,
    initialValueFetched,
    labelFieldName,
    labelFormatter,
    valueFieldName,
    lookupFieldName,
    getLabel,
  ]);

  const resetSearch = useCallback(() => {
    setSearch("");
    setPage(1);
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const target = e.currentTarget;
      if (
        !isLoading &&
        page &&
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 200
      ) {
        fetchMoreOptions();
      }
    },
    [isLoading, page, fetchMoreOptions],
  );

  const fetchMissingValues = useCallback(
    async (missingValues: (string | number)[]) => {
      const lookupValues = missingValues.filter(
        (val) => typeof val === "number" || !isNaN(Number(val)),
      );
      if (lookupValues.length === 0) return;

      try {
        // @ts-expect-error supress pagination params on lookup request
        const response = await fetchFn({
          ...(defaultFilters || {}),
          [lookupFieldName]: lookupValues,
          pagination: "off",
        });

        const results = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        onFetchSuccess?.(results);
        const newOptions = results
          .filter(
            (item) =>
              item &&
              (labelFormatter ||
                item[labelFieldName as keyof T] !== undefined) &&
              item[valueFieldName as keyof T] !== undefined,
          )
          .map((item) => ({
            ...item,
            label: getLabel(item),
            value: item[valueFieldName as keyof T] as string | number,
          }));

        setOptions((prev) => [
          ...prev,
          ...newOptions.filter(
            (newOpt) =>
              !prev.some((existing) => existing.value === newOpt.value),
          ),
        ]);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { missingValues, defaultFilters },
        });
      }
    },
    [
      fetchFn,
      defaultFilters,
      lookupFieldName,
      labelFieldName,
      labelFormatter,
      valueFieldName,
      getLabel,
      onFetchSuccess,
    ],
  );

  useEffect(() => {
    if (!initialValueFetched || !initialValue) return;

    const values = Array.isArray(initialValue) ? initialValue : [initialValue];
    const missing = values.filter(
      (val) =>
        !optionsRef.current.some((opt) => String(opt.value) === String(val)),
    );

    if (missing.length > 0) {
      fetchMissingValues(missing);
    }
  }, [initialValue, initialValueFetched, fetchMissingValues]);

  useEffect(() => {
    fetchInitialValues();
  }, [fetchInitialValues]);

  return {
    options,
    isLoading,
    search,
    setSearch: handleSearch,
    handleScroll,
    resetSearch,
    fetchOptions: fetchMoreOptions,
    initialValueFetched,
  };
};

export default useInfiniteOptions;
